import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { requireAuthBeforeLoad } from '@/features/auth/guards'
import { AiProcessingQuotaCard } from '@/features/billing/components/ai-processing-quota-card'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'
import {
  formatMembershipExpiresAt,
  formatMembershipPlan,
  formatMembershipStatus,
  hasSubscriptionMembership,
} from '@/features/billing/utils/membership-labels'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { ProjectCard } from '@/features/projects/components/project-card'
import { useProjectActions } from '@/features/projects/hooks/use-project-actions'
import { useProjects, useProjectsLoading, useProjectsError } from '@/features/projects/hooks/use-project-selectors'
import type { Project } from '@/types/project'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ProjectForm } from '@/features/projects/components/project-form'
import type { ProjectFormData } from '@/features/projects/utils/validation'
import { createLogger } from '@/shared/logging/logger'
import { toast } from 'sonner'

const logger = createLogger('AccountRoute')

export const Route = createFileRoute('/account')({
  beforeLoad: ({ location }) => requireAuthBeforeLoad({ location }),
  component: AccountRoute,
})

function AccountRoute() {
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const session = useAuthSessionStore((state) => state.session)
  const openMembershipDialog = useMembershipDialogStore((state) => state.openDialog)
  const { loadProjects, updateProject } = useProjectActions()
  const projects = useProjects()
  const projectsLoading = useProjectsLoading()
  const projectsError = useProjectsError()
  const planLabel = formatMembershipPlan(session?.membership?.planName, session?.membership?.planCode)
  const statusLabel = formatMembershipStatus(session?.membership?.status)
  const expiresAtLabel = formatMembershipExpiresAt(session?.membership?.expiresAt)
  const showExpiresAt = hasSubscriptionMembership(
    session?.membership?.planCode,
    session?.membership?.expiresAt,
  )
  const recentProjects = useMemo(
    () => [...projects].sort((left, right) => right.updatedAt - left.updatedAt).slice(0, 4),
    [projects],
  )

  useEffect(() => {
    void loadProjects()
  }, [loadProjects])

  const handleEditSubmit = async (data: ProjectFormData) => {
    if (!editingProject) return

    setIsSubmitting(true)
    try {
      await updateProject(editingProject.id, data)
      setEditingProject(null)
    } catch (error) {
      logger.error('Failed to update project from account center:', error)
      toast.error('更新项目失败', { description: '请稍后重试' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!session) {
    return null
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">个人中心</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              这里展示你的基础资料、订阅信息和项目概览，后续会继续补充更多工作台统计。
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/projects">
              <Button variant="outline">返回项目中心</Button>
            </Link>
            <Button onClick={openMembershipDialog}>开通会员</Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <section className="rounded-3xl border border-border bg-card p-6">
            <h2 className="text-xl font-semibold">基础资料</h2>
            <div className="mt-5 flex items-center gap-4">
              {session.user.avatarUrl ? (
                <img
                  src={session.user.avatarUrl}
                  alt={session.user.name}
                  className="h-16 w-16 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
                  {session.user.name.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div>
                <div className="text-lg font-medium">{session.user.name}</div>
                <div className="text-sm text-muted-foreground">{session.user.email}</div>
                <div className="mt-1 text-xs text-muted-foreground">用户 ID: {session.user.id}</div>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-border bg-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">订阅信息</h2>
                <p className="mt-2 text-sm text-muted-foreground">你的会员状态与有效期会在这里实时更新。</p>
              </div>
              <div className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                {statusLabel}
              </div>
            </div>

            <div className="mt-6 grid gap-3">
              <div className="rounded-2xl border border-border/80 bg-background/60 px-4 py-4">
                <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground/80">当前套餐</div>
                <div className="mt-2 text-2xl font-semibold text-foreground">{planLabel}</div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-border/80 bg-background/60 px-4 py-4">
                  <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground/80">会员状态</div>
                  <div className="mt-2 text-base font-semibold text-foreground">{statusLabel}</div>
                </div>

                {showExpiresAt ? (
                  <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-4">
                    <div className="text-xs font-medium uppercase tracking-[0.16em] text-emerald-300/80">到期时间</div>
                    <div className="mt-2 text-base font-semibold text-emerald-100">{expiresAtLabel}</div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-border/80 bg-background/60 px-4 py-4">
                    <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground/80">会员说明</div>
                    <div className="mt-2 text-base font-semibold text-foreground">开通后立即生效</div>
                  </div>
                )}
              </div>
            </div>
          </section>

          <AiProcessingQuotaCard />

          <section className="rounded-3xl border border-border bg-card p-6 lg:col-span-2">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold">项目概览</h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  这里优先展示最近更新的项目，保留和项目中心一致的卡片样式，方便你快速回到手头工作。
                </p>
              </div>
              <Link to="/projects">
                <Button variant="outline">进入完整项目中心</Button>
              </Link>
            </div>

            {projectsError ? (
              <div className="rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-4 text-sm text-destructive">
                项目加载失败：{projectsError}
              </div>
            ) : projectsLoading && projects.length === 0 ? (
              <div className="flex min-h-40 items-center justify-center rounded-2xl border border-border/70 bg-background/40">
                <div className="text-center">
                  <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
                  <p className="text-sm text-muted-foreground">正在加载项目...</p>
                </div>
              </div>
            ) : projects.length === 0 ? (
              <div className="flex min-h-40 flex-col items-center justify-center rounded-2xl border border-border/70 bg-background/40 px-4 text-center">
                <p className="text-base font-medium text-foreground">还没有项目</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  进入项目中心创建或导入项目后，这里会自动显示最近更新内容。
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    最近更新 {recentProjects.length} / 共 {projects.length} 个项目
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {recentProjects.map((project) => (
                    <ProjectCard key={project.id} project={project} onEdit={setEditingProject} />
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>

        <Dialog open={!!editingProject} onOpenChange={(open) => !open && setEditingProject(null)}>
          <DialogContent className="max-h-[90vh] w-[95vw] max-w-[1200px] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">编辑项目</DialogTitle>
              <DialogDescription>更新项目名称、分辨率和基础设置</DialogDescription>
            </DialogHeader>
            {editingProject && (
              <ProjectForm
                onSubmit={handleEditSubmit}
                onCancel={() => setEditingProject(null)}
                defaultValues={{
                  name: editingProject.name,
                  description: editingProject.description,
                  width: editingProject.metadata.width,
                  height: editingProject.metadata.height,
                  fps: editingProject.metadata.fps,
                }}
                isEditing={true}
                isSubmitting={isSubmitting}
                hideHeader
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
